import { useEffect, useRef, useState } from 'react';
import { InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';

import BaseInputValidation from '@/components/ui/Input/BaseInputValidation';
import ForgotPassword from '@/components/Modal/Auth/ForgotPassword';
import BDHelmet from '@/components/BDHelmet';
import store from '@/store';
import auth from '@/api/auth';

import logo from '@/assets/bd-icon-w.png';
import { useNavigate } from 'react-router-dom';

const schema = yup.object().shape({
	// email: yup.string().required('Email is a required field').email('Email not valid'),
	email: yup.string().required(),
	password: yup.string().required('Password is a required field'),
});

const { signIn } = auth();

export default function Login() {
	const navigate = useNavigate();
	const [values, setValues] = useState({ showPassword: false });
	const [loading, setLoading] = useState(false);
	const [showForgotPassword, setShowForgotPassword] = useState(false);

	const emailRef = useRef();

	useEffect(() => {
		Cookies.remove('access_token');
		Cookies.remove('refresh_token');

		return () => {};
	}, []);

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			email: '',
			password: '',
		},
	});

	useEffect(() => {
		if (emailRef.current) {
			emailRef.current.focus();
		}
	}, []);

	const handleClickShowPassword = () => {
		setValues({
			...values,
			showPassword: !values.showPassword,
		});
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	const onSubmit = (dataAuth) => {
		setLoading(true);
		signIn({
			username: dataAuth.email.toLowerCase(),
			password: dataAuth.password,
		})
			.then((res) => {
				const { data } = res;
				if (data && res.status === 200) {
					Cookies.set('bdKey', data.access, {
						expires: 1 / 4,
						secure: true,
						sameSite: 'Strict',
					});
					Cookies.set('bdRef', data.refresh, { expires: 1, secure: true, sameSite: 'Strict' });

					store.dispatch({ type: 'auth/setLogin', payload: data });
					navigate('/', { replace: true });
				}
				setLoading(false);
			})
			.catch((err) => {
				const msg = err.response?.data;
				if (msg && Object.keys(msg).length) {
					Object.keys(msg).forEach((key) => {
						toast.error(msg[key] || 'Error');
					});
				}
				setLoading(false);
			});
	};

	return (
		<>
			<BDHelmet title='Login' description='BDS Samferdsel login' />
			<section className='w-screen h-screen overflow-hidden bg-image'>
				<div className='flex flex-wrap items-center justify-center w-1/3 xl:w-[30%] h-full gap-4 ml-auto float-end bg-primary-80 md:gap-8 xl:gap-12'>
					<div className='flex flex-col w-full gap-4 px-4'>
						<div className='flex flex-col w-full text-white'>
							<img className='-ml-[10px] w-fit' src={logo} alt='logo' />
							<span className='mb-2 text-2xl'>BRØDRENE DAHL SAMFERDSEL</span>
							<span className='text-xs'>Logg inn på BDS dokumentkontrollsystem</span>
						</div>
						<form
							onSubmit={handleSubmit(onSubmit)}
							className='flex flex-col w-full p-4 space-y-4 bg-white rounded-lg shadow-lg bg-opacity-80'
							autoComplete='off'
						>
							<h1 className='text-[2rem] font-medium tracking-wide'>LOGG INN</h1>
							<div className='flex flex-col gap-6'>
								<BaseInputValidation
									control={control}
									name='email'
									label='Brukernavn'
									errors={errors}
									ref={emailRef}
									autoFocus
									InputLabelProps={{ shrink: true }}
								/>
								<BaseInputValidation
									control={control}
									name='password'
									label='Passord'
									errors={errors}
									config={{ type: values.showPassword ? 'text' : 'password' }}
									InputLabelProps={{ shrink: true }}
									InputProps={{
										endAdornment: (
											<InputAdornment position='end'>
												<IconButton
													aria-label='toggle password visibility'
													onClick={handleClickShowPassword}
													onMouseDown={handleMouseDownPassword}
													edge='end'
												>
													{values.showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</div>
							<button className='text-right text-primary' type='button' onClick={() => setShowForgotPassword(!showForgotPassword)}>
								Glemt passord?
							</button>
							<button
								className='relative flex items-center justify-center h-10 px-12 mx-auto text-white uppercase rounded-full btn-default bg-primary w-fit'
								type='submit'
							>
								Logg In {loading && <CircularProgress className='ml-2' size={18} sx={{ color: 'var(--white)' }} />}
							</button>
						</form>
					</div>
				</div>
				{showForgotPassword && <ForgotPassword open={showForgotPassword} onClose={() => setShowForgotPassword(false)} />}
			</section>
		</>
	);
}
